import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Empty from 'src/common/components/Empty';
import CompetitionInsightCard from 'src/common/components/insights/CompetitionInsightCard';
import InsightsKpiCards from 'src/common/components/insights/InsightsKpiCards';
import SignupBreakdownSection from 'src/common/components/signup-breakdown/SignupBreakdownSection';
import SignupTimelineSection from 'src/common/components/signup-timeline/SignupTimelineSection';
import { LanguageKeys } from 'src/common/constants/languages';
import { rollupEditionStatistics } from 'src/helpers/statisticExtras';
import { ticketCardGridItemSx } from 'src/common/styles/ticketCardStyles';
import { ICompetition } from 'src/interfaces/Competition';
import { IEdition } from 'src/interfaces/Edition';
import InsightsPanelSurface from 'src/pages/Dashboard/Insights/components/InsightsPanelSurface';
import ParticipantsDistributionMap from 'src/pages/PublicPages/components/ParticipantsDistributionMap';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  edition: IEdition;
  currentLang: LanguageKeys;
  getCompetitionLink: (competitionName: string, year: number) => string;
  showYearHeading?: boolean;
  audience?: 'public' | 'admin';
}

function getFinisherCount(
  edition: IEdition,
  competition: ICompetition,
  editionCompetition: { distances?: string[] },
): number {
  return (editionCompetition.distances ?? []).reduce((sum, distId) => {
    const stat = edition.statistics?.[`${competition.id}:${distId}`];
    if (!stat) return sum;
    if (stat.summary?.totalParticipants != null) {
      return sum + stat.summary.totalParticipants;
    }
    return sum + (stat.dataByGender?.M?.ratio ?? 0) + (stat.dataByGender?.F?.ratio ?? 0);
  }, 0);
}

export default function EditionInsightsHub({
  edition,
  currentLang,
  getCompetitionLink,
  showYearHeading = false,
  audience = 'admin',
}: Props) {
  const { t } = useTranslation();
  const { activeEditionId, competitionsById } = useSystemOptionsStore(s => s);

  const rollup = rollupEditionStatistics(edition.statistics);
  const hasStats = edition.statistics && Object.keys(edition.statistics).length > 0;
  const isCurrentEdition = edition.id === activeEditionId;

  const emptyTitle =
    audience === 'public'
      ? isCurrentEdition
        ? t('race.results.noStatsYetTitle')
        : t('race.results.noStatsPastTitle', { year: edition.year })
      : t('statistics.notEnoughData');

  const emptyDescription =
    audience === 'public'
      ? isCurrentEdition
        ? t('race.results.noStatsYetDescription')
        : t('race.results.noStatsPastDescription')
      : t('insights.noSavedData');

  const competitionsForEdition = (edition.competitions ?? [])
    .map(ec => competitionsById?.[ec.competitionId])
    .filter((c): c is ICompetition => Boolean(c));

  return (
    <Grid container spacing={2} sx={{ mb: showYearHeading ? 4 : 0, overflow: 'visible' }}>
      {showYearHeading ? (
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" component="h2">
            {edition.year}
          </Typography>
        </Grid>
      ) : null}
      <Grid size={{ xs: 12 }}>
        <ParticipantsDistributionMap
          editionId={edition.id}
          editionYear={edition.year}
          hasResultsData={Boolean(edition.statistics)}
          competitions={competitionsForEdition}
          currentLang={currentLang}
        />
      </Grid>
      {audience !== 'public' ? (
        <>
          <Grid size={{ xs: 12 }}>
            <SignupTimelineSection edition={edition} includeUnpublished />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <SignupBreakdownSection edition={edition} includeUnpublished />
          </Grid>
        </>
      ) : null}
      {!hasStats ? (
        <Grid size={{ xs: 12 }}>
          <InsightsPanelSurface>
            <Empty
              embedded
              icon={<BarChartOutlinedIcon fontSize="large" />}
              title={emptyTitle}
              description={emptyDescription}
            />
          </InsightsPanelSurface>
        </Grid>
      ) : (
        <>
          <Grid size={{ xs: 12 }}>
            <InsightsKpiCards {...rollup} />
          </Grid>
          {(edition.competitions ?? []).map(ec => {
            const competition = competitionsById?.[ec.competitionId];
            if (!competition) return null;
            const finisherCount = getFinisherCount(edition, competition, ec);
            return (
              <Grid key={competition.id} size={{ xs: 12, sm: 6, md: 4 }} sx={ticketCardGridItemSx}>
                <CompetitionInsightCard
                  competition={competition}
                  finisherCount={finisherCount}
                  currentLang={currentLang}
                  year={edition.year}
                  getCompetitionLink={getCompetitionLink}
                />
              </Grid>
            );
          })}
        </>
      )}
    </Grid>
  );
}
